import { useTranslation } from 'react-i18next'
import { ImageIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { TemplateSpec, ZnsTemplateDesign } from './zns-template-form'

/** Thay `<ten_tham_so>` bằng giá trị mẫu đã nhập; chưa nhập thì giữ nguyên dạng `<ten>` (in nghiêng ở nơi gọi). */
function fill(text: string | undefined, params: ZnsTemplateDesign['params']): string {
  if (!text) return ''
  return text.replace(/<([^<>\s]+)>/g, (m, name: string) => {
    const p = params.find((x) => x.name === name)
    return p?.sampleValue ? p.sampleValue : m
  })
}

function Placeholder({ label, className }: { label: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-1.5 rounded border border-dashed bg-muted/40 text-[11px] text-muted-foreground', className)}>
      <ImageIcon className="h-3.5 w-3.5 shrink-0" />
      {label}
    </div>
  )
}

/** Ảnh/logo đã chọn trong phiên soạn này → xem được thật (blob URL cục bộ); mẫu đang sửa chưa chọn lại ảnh thì chỉ có media_id của Zalo (không xem lại được) → khung giữ chỗ. */
function PreviewImage({ mediaId, imagePreviews, label, className }: { mediaId: string | undefined; imagePreviews: Record<string, string>; label: string; className?: string }) {
  if (!mediaId) return null
  const src = imagePreviews[mediaId]
  if (!src) return <Placeholder label={label} className={className} />
  return <img src={src} alt={label} className={cn('rounded border object-cover', className)} />
}

export function ZnsTemplatePreview({
  design, spec, imagePreviews,
}: {
  design: ZnsTemplateDesign
  spec: TemplateSpec | undefined
  imagePreviews: Record<string, string>
}) {
  const { t } = useTranslation('marketing')
  const typeLabel = spec?.templateTypes.find((x) => x.value === design.templateType)?.label
  const tagLabel = spec?.tags.find((x) => x.value === design.tag)?.label
  const images = design.header.images ?? []
  const buttons = design.buttons ?? []

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary">{t('znsTemplateEditor.preview.label')}</Badge>
        {typeLabel && <Badge variant="outline">{typeLabel}</Badge>}
        {tagLabel && <Badge variant="outline">{tagLabel}</Badge>}
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        {design.header.logo ? (
          <div className="flex h-12 items-center gap-2 border-b bg-muted/20 px-3">
            <PreviewImage mediaId={design.header.logo.light} imagePreviews={imagePreviews} label={t('znsTemplateEditor.preview.logo')} className="h-7 w-7 shrink-0" />
            <span className="truncate text-xs font-medium text-muted-foreground">{t('znsTemplateEditor.preview.logoHeader')}</span>
          </div>
        ) : images.length > 0 ? (
          <div className={cn('grid gap-0.5', images.length === 1 ? 'grid-cols-1' : images.length === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
            {images.map((id, i) => (
              <PreviewImage key={i} mediaId={id} imagePreviews={imagePreviews} label={t('znsTemplateEditor.preview.imageN', { n: i + 1 })} className="aspect-video w-full rounded-none border-0" />
            ))}
          </div>
        ) : (
          <Placeholder label={t('znsTemplateEditor.preview.noHeader')} className="h-16 rounded-none border-0 border-b" />
        )}

        <div className="space-y-2.5 p-3.5 text-sm">
          {design.templateType === 2 ? (
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-center text-lg font-semibold tracking-[0.3em]">
              {fill(design.otp, design.params) || '••••••'}
            </div>
          ) : design.title ? (
            <p className="font-semibold leading-snug">{fill(design.title, design.params)}</p>
          ) : (
            <p className="italic text-muted-foreground">{t('znsTemplateEditor.preview.noTitle')}</p>
          )}

          {(design.paragraphs ?? []).map((p, i) => (
            <p key={i} className="whitespace-pre-wrap leading-snug text-foreground/90">{fill(p, design.params)}</p>
          ))}

          {!!design.table?.length && (
            <div className="space-y-1 rounded-lg border p-2 text-xs">
              {design.table.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{r.title}</span>
                  <span className="font-medium">{fill(r.value, design.params)}</span>
                </div>
              ))}
            </div>
          )}

          {design.voucher && (
            <div className="space-y-1 rounded-lg border border-dashed p-2.5 text-xs">
              <p className="font-semibold">{fill(design.voucher.name, design.params) || t('znsTemplateEditor.preview.voucherPlaceholder')}</p>
              {design.voucher.condition && <p className="text-muted-foreground">{fill(design.voucher.condition, design.params)}</p>}
              <div className="flex items-center justify-between text-muted-foreground">
                <span>{fill(design.voucher.voucherCode, design.params) || '— —'}</span>
                <span>{t('znsTemplateEditor.preview.voucherExpires', { date: fill(design.voucher.endDate, design.params) || '—' })}</span>
              </div>
            </div>
          )}

          {design.payment && (
            <div className="space-y-1 rounded-lg border p-2.5 text-xs">
              <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('znsTemplateEditor.preview.paymentAccount')}</span><span className="font-medium">{design.payment.accountName || '—'}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('znsTemplateEditor.preview.paymentNumber')}</span><span className="font-medium">{design.payment.bankAccount || '—'}</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('znsTemplateEditor.preview.paymentAmount')}</span><span className="font-medium">{fill(design.payment.amount, design.params) || '—'}</span></div>
            </div>
          )}

          {design.rating && (
            <div className="flex items-center gap-1 text-lg">
              {Array.from({ length: design.rating.items.length }, (_, i) => <span key={i}>⭐</span>)}
            </div>
          )}
        </div>

        {buttons.length > 0 && (
          <div className="divide-y border-t">
            {buttons.map((b, i) => (
              <div key={i} className="px-3 py-2.5 text-center text-sm font-medium text-primary">
                {b.title || t('znsTemplateEditor.preview.buttonPlaceholder')}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
